import { useEffect, useId, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { sanitizeLogoUrl, useAuthUi } from "./context";
import type { ActionResult } from "./types";

export function AuthShell({ title, intro, children, wide = false }: { title: string; intro?: string; children: ReactNode; wide?: boolean }) {
  const { client, locale, setLocale, t } = useAuthUi();
  const logo = sanitizeLogoUrl(client.logoUrl);
  return <div className="auth-page">
    <a className="skip-link" href="#auth-main">{locale === "en" ? "Skip to content" : "Pular para o conteúdo"}</a>
    <header className="site-header">
      <a className="wordmark" href="/" aria-label="Elefante">{t("brand")}</a>
      <label className="locale-picker">
        <span>{t("localeLabel")}</span>
        <select value={locale} onChange={(event) => setLocale(event.target.value as "pt-BR" | "en")}>
          <option value="pt-BR">{t("portuguese")}</option>
          <option value="en">{t("english")}</option>
        </select>
      </label>
    </header>
    <main id="auth-main" tabIndex={-1} className={wide ? "auth-card auth-card--wide" : "auth-card"}>
      <div className="app-identity">
        {logo ? <img src={logo} alt="" width="48" height="48" referrerPolicy="no-referrer" /> : <span className="app-initial" aria-hidden="true">{client.name.trim().charAt(0).toUpperCase()}</span>}
        <div><p className="eyebrow">Elefante Auth</p><h1>{title}</h1></div>
      </div>
      {intro ? <p className="intro">{intro}</p> : null}
      {children}
    </main>
    <footer><a href="/privacy">{t("privacy")}</a><a href="/terms">{t("terms")}</a></footer>
  </div>;
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
    <circle cx="12" cy="12" r="2.5" />
    {visible ? <path d="m4 4 16 16" /> : null}
  </svg>;
}

export function TextField({ label, error, hint, type, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | undefined; hint?: string | undefined }) {
  const { t } = useAuthUi();
  const id = useId();
  const password = type === "password";
  const [passwordVisible, setPasswordVisible] = useState(false);
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : "", error ? errorId : ""].filter(Boolean).join(" ") || undefined;
  return <div className={`field${password ? " field--password" : ""}`}>
    <label htmlFor={id}>{label}</label>
    <div className="field-control">
      <input {...input} id={id} type={password && passwordVisible ? "text" : type} aria-invalid={Boolean(error)} aria-describedby={describedBy} />
      {password ? <button
        className="password-toggle"
        type="button"
        aria-label={t(passwordVisible ? "hidePassword" : "showPassword")}
        aria-pressed={passwordVisible}
        onClick={() => setPasswordVisible((visible) => !visible)}
      ><PasswordVisibilityIcon visible={passwordVisible} /></button> : null}
    </div>
    {hint ? <small id={hintId}>{hint}</small> : null}
    {error ? <small id={errorId} className="field-error">{error}</small> : null}
  </div>;
}

export function SubmitButton({ busy, children, disabled = false, tone = "primary" }: { busy: boolean; children: ReactNode; disabled?: boolean; tone?: "primary" | "danger" | "secondary" }) {
  const { t } = useAuthUi();
  return <button className={`button button--${tone}`} type="submit" disabled={busy || disabled} aria-busy={busy}>{busy ? t("loading") : children}</button>;
}

export function ActionButton({ busy, children, disabled = false, onClick, tone = "secondary" }: { busy?: boolean; children: ReactNode; disabled?: boolean; onClick: () => void; tone?: "primary" | "danger" | "secondary" }) {
  const { t } = useAuthUi();
  return <button className={`button button--${tone}`} type="button" disabled={busy || disabled} aria-busy={busy} onClick={onClick}>{busy ? t("loading") : children}</button>;
}

export function ResultNotice({ result }: { result: ActionResult | null }) {
  const notice = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!result?.message || result.ok || !notice.current) return;
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth";
    notice.current.focus({ preventScroll: true });
    notice.current.scrollIntoView({ behavior, block: "center" });
  }, [result]);
  if (!result?.message) return null;
  return <div
    ref={notice}
    className={result.ok ? "notice notice--success" : "notice notice--error"}
    role={result.ok ? "status" : "alert"}
    tabIndex={result.ok ? undefined : -1}
  >{result.message}</div>;
}

export function useAction() {
  const { t, onNavigate } = useAuthUi();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ActionResult | null>(null);
  async function run(action: () => Promise<ActionResult>): Promise<ActionResult> {
    setBusy(true);
    setResult(null);
    try {
      const next = await action();
      const normalized: ActionResult = next.ok || next.message ? next : { ...next, message: t("genericError") };
      setResult(normalized);
      if (next.ok && next.redirectTo) onNavigate?.(next.redirectTo);
      return normalized;
    } catch {
      const fallback = { ok: false, message: t("genericError") };
      setResult(fallback);
      return fallback;
    } finally {
      setBusy(false);
    }
  }
  return { busy, result, run, setResult };
}

export function Form({ onSubmit, children, className = "form" }: { onSubmit: () => unknown; children: ReactNode; className?: string }) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onSubmit();
  };
  return <form className={className} onSubmit={submit} noValidate>{children}</form>;
}

export function Divider({ label }: { label: string }) {
  return <div className="divider"><span>{label}</span></div>;
}
